"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.Database = exports.DatabaseNamespace = void 0;
const encoding_down_1 = __importDefault(require("encoding-down"));
const levelup_1 = __importDefault(require("levelup"));
const bytes_1 = require("../utils/bytes");
const encryption_1 = require("../utils/encryption");
var DatabaseNamespace;
(function (DatabaseNamespace) {
    DatabaseNamespace["ChainSyncInfo"] = "chain_sync_info";
})(DatabaseNamespace = exports.DatabaseNamespace || (exports.DatabaseNamespace = {}));
/** Database class */
class Database {
    /**
     * Create a Database object from levelDB store
     * @param leveldown - abstract-leveldown compatible store
     */
    constructor(leveldown) {
        // Create levelDB database from leveldown store
        this.level = (0, levelup_1.default)((0, encoding_down_1.default)(leveldown));
    }
    isClosed() {
        return this.level.isClosed();
    }
    /**
     * Parses path and returns key
     * @param path - path to convert
     * @returns key
     */
    static pathToKey(path) {
        // Convert to hex string, pad to 32 bytes, and join with :
        return path.map((el) => (0, bytes_1.hexlify)(el).toLowerCase().padStart(64, '0')).join(':');
    }
    /**
     * Set value in database
     * @param path - database path
     * @param value - value to set
     * @param encoding - data encoding to use
     * @returns complete
     */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    put(path, value, encoding = 'hex') {
        const key = Database.pathToKey(path);
        return this.level.put(key, value, { valueEncoding: encoding });
    }
    /**
     * Get value from database
     * @param path - database path
     * @param encoding - data encoding to use
     * @returns value
     */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    get(path, encoding = 'hex') {
        const key = Database.pathToKey(path);
        return this.level.get(key, { valueEncoding: encoding });
    }
    /**
     * Delete value from database
     * @param path - database path
     * @param encoding - data encoding to use
     * @returns complete
     */
    del(path, encoding = 'hex') {
        const key = Database.pathToKey(path);
        return this.level.del(key, { valueEncoding: encoding });
    }
    /**
     * Perform batch operation on database
     * @param ops - operations to perform
     * @param encoding - data encoding to use
     * @returns complete
     */
    batch(ops, encoding = 'hex') {
        return this.level.batch(ops, { valueEncoding: encoding });
    }
    /**
     * Set encrypted value in database
     * @param path - database path
     * @param encryptionKey - AES-256-GCM encryption key
     * @param value - value to encrypt and set
     */
    async putEncrypted(path, encryptionKey, value) {
        // Encrypt data
        const encrypted = encryption_1.AES.encryptGCM((0, bytes_1.chunk)(value), encryptionKey);
        // Write to database
        await this.put(path, encrypted, 'json');
    }
    /**
     * Get encrypted value in database
     * @param path - database path
     * @param encryptionKey - AES-256-GCM  encryption key
     * @return decrypted value
     */
    async getEncrypted(path, encryptionKey) {
        // Read from database
        const encrypted = (await this.get(path, 'json'));
        // Decrypt and return
        return (0, bytes_1.combine)(encryption_1.AES.decryptGCM(encrypted, encryptionKey));
    }
    /**
     * Gets stream of keys and/or values in namespace
     * @param namespace - namespace to stream from
     * @returns namespace stream
     */
    streamNamespace(namespace, keys = true, values = false) {
        const pathkey = Database.pathToKey(namespace);
        return this.level.createReadStream({
            gte: `${pathkey}`,
            lte: `${pathkey}~`,
            keys,
            values,
        });
    }
    /**
     * Gets all keys in namespace
     * @param namespace - namespace to stream from
     * @returns list of keys
     */
    getNamespaceKeys(namespace) {
        return new Promise((resolve) => {
            const keyList = [];
            // Stream list of keys and resolve on end
            this.streamNamespace(namespace)
                .on('data', (key) => {
                keyList.push(key);
            })
                .on('end', () => {
                resolve(keyList);
            });
        });
    }
    /**
     * Delete all keys in namespace
     * @param namespace - namespace to delete
     * @returns complete
     */
    async clearNamespace(namespace) {
        const pathkey = Database.pathToKey(namespace);
        await this.level.clear({
            gte: `${pathkey}`,
            lte: `${pathkey}~`,
        });
    }
    /**
     * Counnts number of keys in namespace
     * @param namespace - namespace to count keys in
     * @returns number of keys in namespace
     */
    countNamespace(namespace) {
        return new Promise((resolve) => {
            let keyNumber = 0;
            // Create read stream for namespace*
            this.streamNamespace(namespace)
                .on('data', () => {
                // Increment keynumber
                keyNumber += 1;
            })
                .on('end', () => {
                // Return keynumber
                resolve(keyNumber);
            });
        });
    }
    /**
     * Closes DB connections and cleans up listeners
     */
    async close() {
        await this.level.close();
    }
}
exports.Database = Database;
